import { useEffect } from 'react';
import {
  type Control,
  type FieldPath,
  type FieldValues,
  type UseFormSetValue,
  type UseFormWatch,
} from 'react-hook-form';
import { CheckboxField, TextInputField } from '@brighte/form-components';
import {
  type AddressType,
  EnterpriseStructure,
  IdentificationType,
  type IdentityType,
  isBusinessLoanProduct,
} from '@brighte/validation';
import { Address } from '@components/Address';
import { Text } from '@spark-web/text';
import { InputAdornment } from '@spark-web/text-input';
import { LABELS, OWNERSHIP_DETAILS } from '../../utils';
import IdentificationForm from '../Identification/IdentificationForm';

interface BeneficialIdentificationProps<TFieldValues extends FieldValues = FieldValues> {
  title: string;
  control: Control<TFieldValues>;
  name: string;
  watch: UseFormWatch<TFieldValues>;
  businessType: EnterpriseStructure;
  setValue?: UseFormSetValue<TFieldValues>;
  defaultValues?: Record<string, unknown>;
  financialProductId?: string;
  clearErrors?: (name?: string | string[]) => void;
}

function BeneficialIdentification<TFieldValues extends FieldValues = FieldValues>({
  title,
  control,
  name,
  watch,
  businessType,
  setValue,
  defaultValues,
  financialProductId,
  clearErrors,
}: BeneficialIdentificationProps<TFieldValues>) {
  const { HOME_ADDRESS, OWNERSHIP_PERCENTAGE } = LABELS;
  const identificationField = `${name}.identification.type` as FieldPath<TFieldValues>;
  const identificationType = watch(identificationField);

  const handlePercentageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const percentage = event.target.value ? parseInt(event.target.value) : '';
    setValue?.(`${name}.ownershipPercentage` as FieldPath<TFieldValues>, percentage as FieldValues[string]);
  };
  const requiredPercentages = [
    EnterpriseStructure.Company,
    EnterpriseStructure.Trust,
    EnterpriseStructure.Partnership,
    EnterpriseStructure.SoleTrader,
    EnterpriseStructure.ProprietaryLimitedCompany,
  ].includes(businessType);

  useEffect(() => {
    if (isBusinessLoanProduct(financialProductId)) {
      clearErrors?.();
      setValue?.(identificationField, IdentificationType.DRIVING_LICENSE as FieldValues[string]);
    }
  }, [identificationField, financialProductId, clearErrors, setValue]);

  return (
    <>
      <Text weight="semibold">{title}</Text>
      <IdentificationForm
        control={control as unknown as Control<IdentityType>}
        watch={watch as unknown as UseFormWatch<IdentityType>}
        name={name}
      />
      <>
        {identificationType && (
          <>
            <Address
              control={control}
              label={HOME_ADDRESS}
              name={`${name}.address` as FieldPath<TFieldValues>}
              setValue={setValue}
              defaultValue={(defaultValues?.address as AddressType) ?? null}
            />
            {requiredPercentages ? (
              <TextInputField
                control={control}
                label={OWNERSHIP_PERCENTAGE}
                name={`${name}.ownershipPercentage` as FieldPath<TFieldValues>}
                type="number"
                onChange={handlePercentageChange}
              >
                <InputAdornment placement="end">
                  <Text>%</Text>
                </InputAdornment>
              </TextInputField>
            ) : null}

            {isBusinessLoanProduct(financialProductId) && (
              <CheckboxField
                name={`${name}.privacyAndCreditStatement` as FieldPath<TFieldValues>}
                control={control}
                checkedValue={OWNERSHIP_DETAILS.PRIVACY_AND_CREDIT_TEXT}
              >
                {OWNERSHIP_DETAILS.PRIVACY_AND_CREDIT_STATEMENT}
              </CheckboxField>
            )}
          </>
        )}
      </>
    </>
  );
}

export default BeneficialIdentification;
